import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Button } from '../../components/Form/Button';
import { Icon, close } from '../../components/Icon/Icon';
import { Modal } from '../../components/Modal/Modal';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { ApiList, ApiSchedule } from '../../types';
import { AlertCollection, AlertMessages } from '../AlertMessages/AlertMessages';
import { AddressSummary } from '../Form/AddressSummary';
import './ReviewAndActivateModal.css';
import { calculateNextOrderDate, formatCalendarDate, formatScheduleText } from './utils';
import { deliveryAddressOf } from './wizard';

/**
 * The last of the five steps: everything that was chosen, in one place, before anything is created.
 * Activating is the first moment this subscription means an order, so it says so in as many words.
 */
export const ReviewAndActivateModal = ({
	list,
	schedule,
	show,
	activating,
	alerts,
	onClose,
	onActivate,
}: {
	list: ApiList;
	schedule?: ApiSchedule;
	show: boolean;
	activating: boolean;
	alerts: AlertCollection;
	onClose: () => void;
	onActivate: () => void;
}): JSX.Element => {
	const { locale } = useConfig();
	const translations = useTranslation();
	const t = { ...translations.subscriptions, generic: translations.generic };
	const address = deliveryAddressOf(schedule);
	const firstOrder = schedule && calculateNextOrderDate(schedule);

	return (
		<Modal className="review-activate-modal" show={show} disableBodyScroll onDismiss={onClose} toggle={onClose}>
			<ContentCard className="review-activate-content">
				<div className="modal-header">
					<div className="modal-header-title">{t.reviewAndActivate}</div>
					<button className="close-btn" onClick={onClose}>
						<Icon shape={{ ...close, fill: 'black' }} size={20} />
					</button>
				</div>
				<div className="modal-body">
					<div className="review-section">
						<strong>{t.stepProducts}</strong>
						{list.items.map((item) => (
							<p key={item.product.id}>
								{item.quantity} × {item.product.name}
							</p>
						))}
					</div>
					<div className="review-section">
						<strong>{t.stepSchedule}</strong>
						{schedule && <p>{formatScheduleText(schedule, locale, t)}</p>}
						{/* the date the first order is created, which is the first thing activating does */}
						{firstOrder && <p>{formatCalendarDate(firstOrder)}</p>}
					</div>
					<div className="review-section">
						<strong>{t.stepAddress}</strong>
						{address && <AddressSummary address={address} />}
					</div>
					<p className="review-notice">{t.reviewNotice}</p>
					<AlertMessages alerts={alerts} />
				</div>
				<div className="modal-footer">
					<Button className="secondary" disabled={activating} onClick={onClose}>
						{t.generic.cancel}
					</Button>
					<Button disabled={activating} onClick={onActivate}>
						{activating ? t.activating : t.activate}
					</Button>
				</div>
			</ContentCard>
		</Modal>
	);
};
